import type { Deliverable, Project } from '../types/portal'

/**
 * The project's headline status, derived from its deliverables so Portal and
 * client can never see contradictory states.
 */
export function projectStatus(project: Project): string {
  const items = project.deliverables

  if (project.archived) return 'Complete'
  if (items.length === 0) return 'Not Started'
  if (items.every((item) => item.status === 'Delivered')) return 'Complete'

  if (items.some((item) => item.status === 'Ready for Review')) return 'Awaiting Client Review'

  /* Only genuinely outstanding feedback counts. Completed requests are history
     and must never keep a project flagged as needing changes. */
  if (items.some((item) => item.changeRequests.some((request) => !request.resolved))) {
    return 'Changes in Progress'
  }

  if (
    items.some(
      (item) =>
        item.stage === 'Review' && (item.status === 'Editing' || item.status === 'In Production'),
    )
  ) {
    return 'Post-Production'
  }

  if (items.some((item) => item.stage === 'Production' && item.status !== 'Not Started')) {
    return 'In Production'
  }

  if (items.some((item) => item.stage === 'Pre-Production' && item.status !== 'Not Started')) {
    return 'Pre-Production'
  }

  const reviewItems = items.filter((item) => item.stage === 'Review')
  const finals = items.filter((item) => item.stage === 'Final Delivery')
  const reviewsDone =
    reviewItems.length > 0 &&
    reviewItems.every((item) => item.status === 'Approved' || item.status === 'Delivered')

  if (reviewsDone && finals.length > 0) return 'Final Delivery'
  if (reviewsDone) return 'Complete'

  if (items.some((item) => item.status !== 'Not Started')) return 'In Production'

  return 'Not Started'
}

export interface ClientHeadline {
  heading: 'Your Next Action' | 'Project Status'
  message: string
  support: string
  /** The deliverable the client should open, when there is one. */
  actionId?: string
  actionLabel?: string
}

const NOTHING_REQUIRED = 'Nothing is required from you right now.'

/**
 * Derives the client's headline from the real state of the project, in the
 * priority order Portal defined: client actions first, then project progress.
 */
export function clientHeadline(
  project: Project,
  visible: Deliverable[],
  complete: boolean,
): ClientHeadline {
  const awaiting = visible.filter((item) => item.status === 'Ready for Review')

  if (awaiting.length === 1) {
    const item = awaiting[0]
    return {
      heading: 'Your Next Action',
      message: `${item.name} is ready for your review.`,
      support: item.version
        ? `Please review ${item.version} and approve it or request changes.`
        : 'Please review the latest version and approve it or request changes.',
      actionId: item.id,
      actionLabel: item.version ? `Review ${item.version}` : `Review ${item.name}`,
    }
  }

  if (awaiting.length > 1) {
    return {
      heading: 'Your Next Action',
      message: `${awaiting.length} deliverables are ready for your review.`,
      support: 'Please review the latest versions and approve them or request changes.',
      actionId: awaiting[0].id,
      actionLabel: 'Start Reviewing',
    }
  }

  if (complete) {
    return {
      heading: 'Project Status',
      message: 'Your project is complete.',
      support: 'All final files are ready.',
    }
  }

  const changing = visible.find((item) => item.status === 'Changes Requested')
  if (changing) {
    return {
      heading: 'Project Status',
      message: `We’re making the changes requested for ${changing.name}.`,
      support: 'We’ll let you know when the next version is ready.',
    }
  }

  const inEdit = visible.find(
    (item) => item.stage === 'Review' && (item.status === 'Editing' || item.status === 'In Production'),
  )

  const anyProduction = visible.some(
    (item) => item.stage === 'Production' && item.status !== 'Approved',
  )
  const anyPreProduction = visible.some((item) => item.stage === 'Pre-Production')

  if (!inEdit && anyPreProduction && !anyProduction) {
    return {
      heading: 'Project Status',
      message: 'We’re currently in pre-production.',
      support: NOTHING_REQUIRED,
    }
  }

  if (!inEdit && anyProduction) {
    return {
      heading: 'Project Status',
      message: 'Production is underway.',
      support: NOTHING_REQUIRED,
    }
  }

  if (inEdit) {
    return {
      heading: 'Project Status',
      message: `${inEdit.name} is now in post-production.`,
      support: 'We’ll let you know when it’s ready for review.',
    }
  }

  const reviewItems = visible.filter((item) => item.stage === 'Review')
  const allApproved =
    reviewItems.length > 0 &&
    reviewItems.every((item) => item.status === 'Approved' || item.status === 'Delivered')
  const finalsPending = visible.some(
    (item) =>
      item.stage === 'Final Delivery' &&
      item.status !== 'Ready for Download' &&
      item.status !== 'Delivered',
  )

  if (allApproved && finalsPending) {
    return {
      heading: 'Project Status',
      message: 'Everything is approved. We’re preparing your final files.',
      support: NOTHING_REQUIRED,
    }
  }

  const approved = [...visible].reverse().find((item) => item.status === 'Approved')
  if (approved) {
    return {
      heading: 'Project Status',
      message: `${approved.name} is approved.`,
      support: NOTHING_REQUIRED,
    }
  }

  if (visible.length === 0) {
    return {
      heading: 'Project Status',
      message: 'We’re setting your project up.',
      support: NOTHING_REQUIRED,
    }
  }

  return {
    heading: 'Project Status',
    message: `${project.name} is underway.`,
    support: NOTHING_REQUIRED,
  }
}
